import React, { useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  collectCollapsibleGroupIds,
  type PipeGroupField,
  type PipeGroupTreeNode,
} from '../utils/groupPipeMaterials';

export type PipeGroupNavPanelProps = {
  tree: PipeGroupTreeNode[];
  collapsedIds: Set<string>;
  selectedGroupId: string | null;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  formatLabel: (field: PipeGroupField, label: string) => string;
};

const depthBg = (depth: number) =>
  cn(
    depth === 0 && 'bg-gray-100 hover:bg-gray-100/90',
    depth === 1 && 'bg-gray-50 hover:bg-gray-100/60',
    depth >= 2 && 'bg-white hover:bg-gray-50'
  );

export const PipeGroupNavPanel: React.FC<PipeGroupNavPanelProps> = ({
  tree,
  collapsedIds,
  selectedGroupId,
  onToggle,
  onSelect,
  onExpandAll,
  onCollapseAll,
  formatLabel,
}) => {
  const allExpanded = useMemo(() => {
    const collapsible = collectCollapsibleGroupIds(tree);
    if (collapsible.size === 0) return true;
    return Array.from(collapsible).every((id) => !collapsedIds.has(id));
  }, [tree, collapsedIds]);

  const renderNodes = (nodes: PipeGroupTreeNode[]) =>
    nodes.map((node) => {
      if (node.type === 'leaf') return null;

      const collapsed = collapsedIds.has(node.id);
      const Chevron = collapsed ? ChevronRight : ChevronDown;
      const selected = selectedGroupId === node.id;
      const hasGroupChildren = node.children.some((c) => c.type === 'group');

      return (
        <li key={node.id}>
          <div
            className={cn(
              'flex cursor-pointer select-none items-center gap-1 border-b border-[#E2E5EA] py-2.5 pr-2',
              selected ? 'bg-gray-200 hover:bg-gray-200' : depthBg(node.depth)
            )}
            style={{ paddingLeft: 8 + node.depth * 16 }}
          >
            {hasGroupChildren ? (
              <button
                type="button"
                className="shrink-0 rounded p-0.5 text-heritage-grey hover:bg-gray-200/60"
                aria-label={collapsed ? 'Expand group' : 'Collapse group'}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(node.id);
                }}
              >
                <Chevron className="h-4 w-4" />
              </button>
            ) : (
              <span className="inline-block w-5 shrink-0" />
            )}
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-semibold text-blueprint-blue"
              onClick={() => onSelect(node.id)}
            >
              <span className="truncate whitespace-nowrap">
                {formatLabel(node.field, node.label)}
              </span>
              <span className="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
                {node.count}
              </span>
            </button>
          </div>
          {!collapsed && hasGroupChildren ? (
            <ul className="m-0 list-none p-0">{renderNodes(node.children)}</ul>
          ) : null}
        </li>
      );
    });

  return (
    <nav
      className="flex min-h-0 w-72 shrink-0 flex-col self-stretch overflow-hidden border-r border-[#E2E5EA]"
      aria-label="Pipe groups"
    >
      <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-2 border-b border-[#E2E5EA] bg-white px-2 py-2">
        <span className="truncate text-xs font-semibold uppercase tracking-wide text-heritage-grey">
          Groups
        </span>
        <button
          type="button"
          className="shrink-0 rounded px-1.5 py-1 text-[11px] font-semibold text-blueprint-blue hover:bg-gray-100"
          onClick={allExpanded ? onCollapseAll : onExpandAll}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <ul className="m-0 min-h-0 flex-1 list-none overflow-y-auto overflow-x-hidden p-0">
        {renderNodes(tree)}
      </ul>
    </nav>
  );
};
